import MiniSearch from 'minisearch';
import {localDb} from '../data/localDb.js';

export const SEARCHABLE_COLLECTIONS = [
  'chatMessages',
  'businessMemory',
  'plans',
  'tasks',
  'files',
  'inboxMessages',
  'adCampaignBriefs',
  'projects'
];

// Tier boosting per docs/local-search-architecture.md:
// High intentionality (memory, plans) boosted over incidental chat messages
export const SEARCH_BOOSTS = {
  businessMemory: 3.0,
  plans: 2.2,
  files: 1.6,
  tasks: 1.4,
  projects: 1.4,
  adCampaignBriefs: 1.2,
  inboxMessages: 1.0,
  chatMessages: 0.7
};

export const MINISEARCH_OPTIONS = {
  fields: ['title', 'text', 'tags', 'module', 'status'],
  storeFields: [
    'id',
    'recordId',
    'collection',
    'title',
    'text',
    'tags',
    'module',
    'status',
    'created',
    'visibility'
  ],
  searchOptions: {
    boost: { title: 2.5, tags: 1.8, text: 1.0 },
    prefix: true,
    fuzzy: (term) => (term.length > 3 ? 0.2 : false)
  }
};

let miniSearchInstance = new MiniSearch(MINISEARCH_OPTIONS);
let saveTimer = null;
let initialized = false;

export function toSearchDoc(collection, record) {
  if (!record || !record.id) return null;

  switch (collection) {
    case 'businessMemory':
      return {
        id: `businessMemory:${record.id}`,
        recordId: record.id,
        collection: 'businessMemory',
        title: record.content ? (record.content.length > 60 ? `${record.content.slice(0, 60)}…` : record.content) : 'Business Memory',
        text: record.content || '',
        tags: Array.isArray(record.tags) ? record.tags.join(' ') : (record.tags || ''),
        module: record.module || 'headquarters',
        status: record.extractionMethod || 'pinned',
        created: record.created || '',
        visibility: null
      };

    case 'plans':
      return {
        id: `plans:${record.id}`,
        recordId: record.id,
        collection: 'plans',
        title: record.title || 'Plan',
        text: `${record.title || ''} ${typeof record.content === 'object' ? JSON.stringify(record.content) : (record.content || '')}`,
        tags: record.project || record.sourcePlanId || '',
        module: record.module || 'finance',
        status: record.status || 'draft',
        created: record.created || '',
        visibility: null
      };

    case 'tasks':
      return {
        id: `tasks:${record.id}`,
        recordId: record.id,
        collection: 'tasks',
        title: record.title || 'Task',
        text: `${record.title || ''} ${record.due || record.due_date || ''} ${record.owner || ''}`,
        tags: record.project || record.owner || '',
        module: record.module || 'tasks',
        status: record.status || 'todo',
        created: record.created || '',
        visibility: null
      };

    case 'files':
      return {
        id: `files:${record.id}`,
        recordId: record.id,
        collection: 'files',
        title: record.name || 'File',
        text: `${record.name || ''} ${record.size || ''} ${record.knowledgeScope || ''}`,
        tags: Array.isArray(record.tags) ? record.tags.join(' ') : (record.tags || ''),
        module: 'files',
        status: record.size || '',
        created: record.created || '',
        visibility: Array.isArray(record.visibility) ? record.visibility : null
      };

    case 'inboxMessages':
      return {
        id: `inboxMessages:${record.id}`,
        recordId: record.id,
        collection: 'inboxMessages',
        title: record.subject || 'Inbox message',
        text: `${record.subject || ''} ${record.preview || ''} ${record.from || ''} ${record.body || ''}`,
        tags: record.from || '',
        module: 'inbox',
        status: record.status || record.triage_status || 'new',
        created: record.created || '',
        visibility: null
      };

    case 'adCampaignBriefs':
      return {
        id: `adCampaignBriefs:${record.id}`,
        recordId: record.id,
        collection: 'adCampaignBriefs',
        title: record.title || 'Ad Campaign',
        text: `${record.title || ''} ${record.targetAudience || ''} ${record.keyMessage || ''} ${Array.isArray(record.variants) ? record.variants.join(' ') : ''}`,
        tags: record.project || '',
        module: 'ads',
        status: record.status || 'draft',
        created: record.created || '',
        visibility: null
      };

    case 'chatMessages':
      return {
        id: `chatMessages:${record.id}`,
        recordId: record.id,
        collection: 'chatMessages',
        title: `Chat in ${record.module || 'Headquarters'}`,
        text: record.text || '',
        tags: Array.isArray(record.attachments) ? record.attachments.map((a) => a.name).join(' ') : '',
        module: record.module || 'headquarters',
        status: record.sender || 'bot',
        created: record.created || '',
        visibility: null
      };

    case 'projects':
      return {
        id: `projects:${record.id}`,
        recordId: record.id,
        collection: 'projects',
        title: record.name || 'Project',
        text: `${record.name || ''} ${record.description || ''}`,
        tags: record.status || '',
        module: 'projects',
        status: record.status || 'active',
        created: record.created || '',
        visibility: null
      };

    default:
      return null;
  }
}

/**
 * Schedule debounced persistence of serialized index to Dexie search_index_blob
 */
export function scheduleSaveIndex() {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(async () => {
    try {
      if (typeof localDb?.table === 'function') {
        const json = JSON.stringify(miniSearchInstance);
        await localDb.table('search_index_blob').put({
          id: 'primary_search_index',
          json,
          docCount: miniSearchInstance.documentCount,
          updated: new Date().toISOString()
        });
      }
    } catch {
      // Storage unavailable or disabled
    }
  }, 400);
}

/**
 * Add or update a record in the inverted search index incrementally
 */
export function indexAdd(collection, record) {
  if (!SEARCHABLE_COLLECTIONS.includes(collection)) return;
  const doc = toSearchDoc(collection, record);
  if (!doc) return;

  try {
    if (miniSearchInstance.has(doc.id)) {
      miniSearchInstance.replace(doc);
    } else {
      miniSearchInstance.add(doc);
    }
    scheduleSaveIndex();
  } catch {
    // Graceful skip on index collision
  }
}

/**
 * Update a record in the inverted search index incrementally
 */
export function indexUpdate(collection, record) {
  indexAdd(collection, record);
}

/**
 * Remove a record from the inverted search index
 */
export function indexRemove(collection, recordId) {
  if (!SEARCHABLE_COLLECTIONS.includes(collection)) return;
  const docId = `${collection}:${recordId}`;
  try {
    if (miniSearchInstance.has(docId)) {
      miniSearchInstance.discard(docId);
      scheduleSaveIndex();
    }
  } catch {
    // Graceful skip
  }
}

/**
 * Initialize search index: rehydrates from Dexie search_index_blob or builds from state
 */
export async function initSearchIndex(state) {
  if (initialized && miniSearchInstance.documentCount > 0) return miniSearchInstance;

  try {
    if (typeof localDb?.table === 'function') {
      const stored = await localDb.table('search_index_blob').get('primary_search_index');
      if (stored?.json && stored.docCount > 0) {
        miniSearchInstance = MiniSearch.loadJSON(stored.json, MINISEARCH_OPTIONS);
        initialized = true;
        return miniSearchInstance;
      }
    }
  } catch {
    // Fall back to building from state
  }

  if (state) {
    rebuildIndexFromState(state);
  }

  initialized = true;
  return miniSearchInstance;
}

/**
 * Full rebuild of search index from in-memory state
 */
export function rebuildIndexFromState(state) {
  miniSearchInstance = new MiniSearch(MINISEARCH_OPTIONS);

  if (!state) return miniSearchInstance;

  const docs = [];

  // 1. Business Memory
  for (const item of state.businessMemory || []) {
    const doc = toSearchDoc('businessMemory', item);
    if (doc) docs.push(doc);
  }

  // 2. Plans
  for (const item of state.plans || []) {
    const doc = toSearchDoc('plans', item);
    if (doc) docs.push(doc);
  }

  // 3. Tasks
  for (const item of state.tasks || []) {
    const doc = toSearchDoc('tasks', item);
    if (doc) docs.push(doc);
  }

  // 4. Files
  for (const item of state.files || []) {
    const doc = toSearchDoc('files', item);
    if (doc) docs.push(doc);
  }

  // 5. Inbox
  for (const item of state.inbox || state.inboxMessages || []) {
    const doc = toSearchDoc('inboxMessages', item);
    if (doc) docs.push(doc);
  }

  // 6. Ad briefs
  for (const item of state.adCampaignBriefs || []) {
    const doc = toSearchDoc('adCampaignBriefs', item);
    if (doc) docs.push(doc);
  }

  // 7. Projects
  for (const item of state.projects || []) {
    const doc = toSearchDoc('projects', item);
    if (doc) docs.push(doc);
  }

  // 8. Chat messages
  const allMessages = Object.values(state.messages || {}).flat();
  for (const item of allMessages) {
    const doc = toSearchDoc('chatMessages', item);
    if (doc) docs.push(doc);
  }

  if (docs.length) {
    miniSearchInstance.addAll(docs);
  }

  scheduleSaveIndex();
  return miniSearchInstance;
}

/**
 * Autocomplete suggestions for type-ahead input
 */
export function suggestSearch(query, limit = 6) {
  if (!query || !query.trim()) return [];
  try {
    const suggestions = miniSearchInstance.autoSuggest(query.trim(), {
      prefix: true,
      fuzzy: 0.2
    });
    return suggestions.slice(0, limit).map((s) => s.suggestion);
  } catch {
    return [];
  }
}

/**
 * Main search query with permission & scope filtering at query time,
 * and collection relevance boosting.
 */
export function searchWorkspace(query, {
  module = null,
  collection = null,
  visibilityBotId = null,
  limit = 25
} = {}) {
  if (!query || !query.trim()) return [];

  const rawMatches = miniSearchInstance.search(query.trim(), {
    prefix: true,
    fuzzy: (term) => (term.length > 3 ? 0.2 : false)
  });

  const enriched = [];

  for (const match of rawMatches) {
    // 1. Filter by collection if specified
    if (collection && match.collection !== collection) continue;

    // 2. Filter by module if specified
    if (module && match.module && match.module !== module) continue;

    // 3. Permission & visibility scoping: if visibility is restricted, verify bot
    if (visibilityBotId && Array.isArray(match.visibility) && match.visibility.length > 0) {
      if (!match.visibility.includes(visibilityBotId)) continue;
    }

    // 4. Calculate boosted score based on intentionality hierarchy
    const collectionMultiplier = SEARCH_BOOSTS[match.collection] || 1.0;
    const finalScore = match.score * collectionMultiplier;

    enriched.push({
      id: match.id,
      recordId: match.recordId,
      collection: match.collection,
      title: match.title,
      text: match.text,
      tags: match.tags,
      module: match.module,
      status: match.status,
      created: match.created,
      score: finalScore,
      moduleRoute: resolveResultRoute(match.collection, match.module)
    });
  }

  // Sort by boosted score descending
  enriched.sort((a, b) => b.score - a.score);

  return enriched.slice(0, limit);
}

function resolveResultRoute(collection, module) {
  switch (collection) {
    case 'businessMemory':
    case 'chatMessages':
      return module || 'headquarters';
    case 'plans':
      return module || 'finance';
    case 'tasks':
      return 'tasks';
    case 'files':
      return 'files';
    case 'inboxMessages':
      return 'inbox';
    case 'adCampaignBriefs':
      return 'ads';
    case 'projects':
      return 'projects';
    default:
      return module || 'headquarters';
  }
}

export function getSearchStats() {
  return {
    documentCount: miniSearchInstance.documentCount,
    initialized
  };
}
